/**
 * 权限检查工具函数
 * 提供统一的权限检查和验证方法
 * 遵循"不显示无权限操作入口"的用户体验原则
 */

/**
 * 检查用户是否具有管理员访问权限
 * @returns {boolean} 是否有管理员权限
 */
export function hasAdminAccess() {
  try {
    return localStorage.getItem('hasAdminAccess') === 'true';
  } catch (error) {
    console.error('检查管理员权限失败:', error);
    return false;
  }
}

/**
 * 检查用户是否具有特定角色
 * @param {string} role 角色名称
 * @returns {boolean} 是否具有该角色
 */
export function hasRole(role) {
  try {
    const userRoles = localStorage.getItem('userRoles');
    if (!userRoles) return false;
    
    const roles = JSON.parse(userRoles);
    return roles.includes(role);
  } catch (error) {
    console.error('检查用户角色失败:', error);
    return false;
  }
}

/**
 * 检查用户是否为管理员
 * @returns {boolean} 是否为管理员
 */
export function isAdmin() {
  return hasRole('ADMIN');
}

/**
 * 检查用户是否为监控员
 * @returns {boolean} 是否为监控员
 */
export function isMonitor() {
  return hasRole('MONITOR');
}

/**
 * 获取用户所有角色
 * @returns {Array<string>} 用户角色列表
 */
export function getUserRoles() {
  try {
    const userRoles = localStorage.getItem('userRoles');
    return userRoles ? JSON.parse(userRoles) : [];
  } catch (error) {
    console.error('获取用户角色失败:', error);
    return [];
  }
}

/**
 * 检查路由是否需要管理员权限
 * @param {Object} route 路由对象
 * @returns {boolean} 是否需要管理员权限
 */
export function routeRequiresAdmin(route) {
  return route.matched && route.matched.some(record => record.meta.requiresAdmin);
}

export function hasRouteRole(route) {
  const required = (route && route.meta && route.meta.roles) || [];
  if (!required.length) return true;
  const roles = getUserRoles();
  if (roles.length) return required.some(r => roles.indexOf(r) !== -1);
  // 兼容：无角色明细时，用 hasAdminAccess(=ADMIN||MONITOR) 判定
  return hasAdminAccess() && required.some(r => r === 'ADMIN' || r === 'MONITOR');
}

/**
 * 权限不足时的处理逻辑
 * @param {Object} router Vue Router实例
 * @param {Object} from 来源路由
 * @param {string} message 错误消息
 */
export function handleInsufficientPermission(router, from, message = '您没有访问该页面的权限') {
  const errorQuery = {
    error: 'no-permission',
    message: message
  };

  // 如果已经在首页，使用replace更新query参数
  if (from.path === '/') {
    router.replace({
      path: '/',
      query: errorQuery
    });
  } else {
    // 如果不在首页，正常重定向
    router.push({
      path: '/',
      query: errorQuery
    });
  }
}

/**
 * 清除权限错误提示
 * @param {Object} router Vue Router实例
 */
export function clearPermissionError(router) {
  const currentPath = router.currentRoute.path;
  router.replace({
    path: currentPath,
    query: {}
  });
}

/**
 * 检查并显示权限错误提示
 * @param {Object} route 当前路由对象
 * @param {Object} router Vue Router实例
 * @param {Object} $message Element UI消息组件
 */
/**
 * 检查并显示权限错误提示
 * @param {Object} route 当前路由对象
 * @param {Object} router Vue Router实例
 * @param {Object} $message Element UI消息组件
 */
export function checkAndShowPermissionError(route, router, $message) {
  const { error, message } = route.query;
  if (error === 'no-permission' && message) {
    // 延迟显示消息，确保组件完全渲染
    setTimeout(() => {
      $message({
        type: 'warning',
        message: message,
        duration: 5000,
        showClose: true
      });
    }, 100);
    
    // 清除URL中的错误参数
    clearPermissionError(router);
  }
}

/**
 * 检查用户是否可以访问特定功能
 * @param {string} feature 功能名称
 * @returns {boolean} 是否可以访问
 */
export function canAccessFeature(feature) {
  const featurePermissions = {
    'dashboard': () => hasAdminAccess(),
    'user-management': () => hasAdminAccess(),
    'bridge-management': () => hasAdminAccess(),
    'vessel-management': () => hasAdminAccess(),
    'device-management': () => hasAdminAccess(),
    'role-management': () => hasAdminAccess(),
    'system-settings': () => hasAdminAccess(),
    'alert-settings': () => hasAdminAccess(),
    'ship-monitor': () => true, // 所有用户都可以访问
    'ship-trajectory': () => true,
    'video-monitor': () => true,
    'bridge-alerts': () => true,
    'bridge-record': () => true
  };
  
  const permissionCheck = featurePermissions[feature];
  return permissionCheck ? permissionCheck() : false;
}

/**
 * 获取用户可访问的功能列表
 * @returns {Array<string>} 可访问的功能列表
 */
export function getAccessibleFeatures() {
  const allFeatures = [
    'dashboard', 'user-management', 'bridge-management', 'vessel-management',
    'device-management', 'role-management', 'system-settings', 'alert-settings',
    'ship-monitor', 'ship-trajectory', 'video-monitor', 'bridge-alerts', 'bridge-record'
  ];
  
  return allFeatures.filter(feature => canAccessFeature(feature));
}

/**
 * 检查路由是否需要权限验证
 * @param {Object} route 路由对象
 * @returns {boolean} 是否需要权限验证
 */
export function routeNeedsPermissionCheck(route) {
  // 公开路由不需要权限检查
  const publicRoutes = ['/login', '/register', '/change-password'];
  if (publicRoutes.includes(route.path)) {
    return false;
  }
  
  // 需要管理员权限的路由
  const adminRoutes = [
    '/dashboard', '/vessel/list', '/vessel/management', '/bridge/list',
    '/bridge/management', '/equipment-all', '/user/list', '/user/management',
    '/user/bridge-association', '/permission/roles', '/settings/system-params',
    '/alert/settings'
  ];
  
  return adminRoutes.some(adminRoute => route.path.startsWith(adminRoute));
}
